/* Progressive enhancement only: without JS every approved chapter stays readable. */
(() => {
  'use strict';
  const instances = new Map();

  function mount(root) {
    if (instances.has(root)) return;
    const slides = Array.from(root.querySelectorAll('[data-story-slide]'));
    const controls = root.querySelector('[data-story-controls]');
    const indicators = Array.from(root.querySelectorAll('[data-story-go]'));
    const toggle = root.querySelector('[data-story-toggle]');
    const label = root.querySelector('[data-story-toggle-label]');
    const status = root.querySelector('[data-story-status]');
    if (slides.length !== 4 || !controls || !toggle || !label || !status) return;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cleanup = [];
    let current = 0;
    let timer = null;
    let manuallyPaused = false;
    let hovering = root.matches(':hover');
    let focused = root.contains(document.activeElement);
    let onScreen = false;
    let destroyed = false;
    const rtl = getComputedStyle(root).direction === 'rtl';

    function listen(target, event, handler) {
      target.addEventListener(event, handler);
      cleanup.push(() => target.removeEventListener(event, handler));
    }

    function schedule() {
      window.clearTimeout(timer);
      timer = null;
      if (destroyed || manuallyPaused || motion.matches || hovering || focused || document.hidden || !onScreen) return;
      // Full reading time per chapter, never a rapid promotional carousel.
      const words = slides[current].textContent.trim().split(/\s+/u).length;
      const delay = Math.max(35000, words * 500 + 10000);
      timer = window.setTimeout(() => show(current + 1, false), delay);
    }

    function updateToggle() {
      const paused = manuallyPaused || motion.matches;
      toggle.dataset.paused = String(paused);
      label.textContent = motion.matches ? toggle.dataset.reducedLabel : paused ? toggle.dataset.resumeLabel : toggle.dataset.pauseLabel;
      toggle.setAttribute('aria-label', label.textContent);
      // Reduced motion is a persistent preference, not an autoplay override.
      toggle.disabled = motion.matches;
    }

    function show(index, manual) {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        indicators[i].setAttribute('aria-current', String(active));
      });
      if (manual) {
        const heading = slides[current].querySelector('h3');
        status.textContent = `${slides[current].dataset.chapterLabel}: ${heading ? heading.textContent : ''}`;
      }
      schedule();
    }

    slides.forEach(slide => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', root.dataset.slideLabel);
      slide.setAttribute('aria-label', slide.dataset.chapterLabel);
    });
    root.setAttribute('aria-roledescription', root.dataset.carouselLabel);
    root.classList.add('is-enhanced');
    controls.hidden = false;
    listen(root.querySelector('[data-story-prev]'), 'click', () => show(current - 1, true));
    listen(root.querySelector('[data-story-next]'), 'click', () => show(current + 1, true));
    indicators.forEach((button, i) => listen(button, 'click', () => show(i, true)));
    listen(toggle, 'click', () => {
      manuallyPaused = !manuallyPaused;
      updateToggle();
      schedule();
    });
    listen(controls, 'keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const forward = rtl ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
      show(current + (forward ? 1 : -1), true);
    });
    listen(root, 'mouseenter', () => { hovering = true; schedule(); });
    listen(root, 'mouseleave', () => { hovering = false; schedule(); });
    listen(root, 'focusin', () => { focused = true; schedule(); });
    listen(root, 'focusout', () => {
      queueMicrotask(() => {
        if (destroyed) return;
        focused = root.contains(document.activeElement);
        schedule();
      });
    });
    listen(document, 'visibilitychange', schedule);
    listen(motion, 'change', () => { updateToggle(); schedule(); });

    // Observe chapters, not the static Heute block. Pause once reading leaves view.
    const viewport = root.querySelector('[data-story-chapters]');
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        onScreen = entries[0].isIntersecting;
        schedule();
      }, { threshold: 0 });
      observer.observe(viewport);
    } else {
      const checkViewport = () => {
        const bounds = viewport.getBoundingClientRect();
        onScreen = bounds.bottom > 0 && bounds.top < window.innerHeight;
        schedule();
      };
      listen(window, 'scroll', checkViewport);
      listen(window, 'resize', checkViewport);
      checkViewport();
    }
    updateToggle();
    show(0, false);
    instances.set(root, () => {
      destroyed = true;
      window.clearTimeout(timer);
      if (observer) observer.disconnect();
      cleanup.forEach(remove => remove());
      instances.delete(root);
    });
  }

  function init(scope) {
    scope.querySelectorAll('[data-founder-story]').forEach(mount);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, root) => {
      if (event.target.contains(root)) destroy();
    });
  });
})();
